import { GridCol, GridContainer, GridRow, Icon, ICONS } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import Heading from '@websrc/App/Components/Heading/Heading'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { hasAsset } from '@websrc/App/Helpers/hasAsset'
import { spotWrapperProps } from '@websrc/App/Helpers/spotWrapperProps'
import { MtmParams } from '@websrc/App/Hooks/useMTMParams'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCaptionSpot } from '@websrc/DataMappers/mapSpot'
import { BackgroundBrick } from '@websrc/Spot/BackgroundBrick/BackgroundBrick'
import { SpotLinkAnchor } from '@websrc/Spot/Link/SpotLinkAnchor'
import React, { useContext } from 'react'

import { LearnMoreButtonType } from '../Collapse/SpotCollapse'
import * as SC from './SpotCaption.style'

export interface SpotCaptionProps {
  spot: ReturnType<typeof mapCaptionSpot>['caption_spot']
  LearnMoreButton?: LearnMoreButtonType
  imageLazy?: boolean
  isTopOfPage?: boolean
}

// eslint-disable-next-line complexity
export function SpotCaption({ LearnMoreButton, spot, imageLazy, isTopOfPage }: SpotCaptionProps) {
  const {
    asset,
    link,
    place_image_under_content,
    cta_type,
    combination_of_mtm_params,
    mtm_params,
  } = spot

  const { matomoPageTemplate } = useContext(AppProviderContext)

  const renderedAsset = (
    <>
      <BackgroundBrick desktop={5} desktopOffset={8} />
      <GridCol>
        <SC.SpotAsset
          lazyLoading={imageLazy}
          sizes={`(min-width: 1440px) 1200px, (min-width: 1024px) 800px, 400px`}
          srcSetWidths={[320, 400, 600, 800, 1200]}
          {...asset}
        />
      </GridCol>
    </>
  )

  const LearnMore = LearnMoreButton && <LearnMoreButton position={[spot.caption, spot.headline]} />

  return (
    <GridContainer>
      <div {...spotWrapperProps(spot.caption)} data-testid={'caption'} />
      <GridRow>
        {hasAsset(asset) && !place_image_under_content && renderedAsset}

        <GridCol desktop={7}>
          <Heading
            headline={spot.headline}
            label={spot.caption}
            level={isTopOfPage ? 1 : 2}
            size={'xl'}
          />
        </GridCol>

        <GridCol desktop={5}>
          <SC.ContentRight>
            {spot.description && (
              <SC.Description>
                <Markdown markdown={spot.description} />
              </SC.Description>
            )}
            <SpotLinkAnchor
              combination_of_mtm_params={combination_of_mtm_params}
              ctaType={cta_type}
              link={link}
              mtm_params={mtm_params as MtmParams}
              onClick={() =>
                matomo.trackEvent(
                  'engagement',
                  matomoPageTemplate,
                  [spot.headline],
                  [link.cta_text],
                  'button'
                )
              }
            >
              {cta_type === 'tertiary' && <Icon icon={ICONS.ARROW_RIGHT} size={16} />}
              <span>{link.cta_text}</span>
            </SpotLinkAnchor>
            {!(hasAsset(asset) && place_image_under_content) && LearnMore && (
              <SC.LearnMoreButtonWrapper>{LearnMore}</SC.LearnMoreButtonWrapper>
            )}
          </SC.ContentRight>
        </GridCol>

        {hasAsset(asset) && place_image_under_content && (
          <>
            {renderedAsset}
            {LearnMore && (
              <SC.LearnMoreButtonWrapper data-testid={'image-under-content'} isUnderImage={true}>
                {LearnMore}
              </SC.LearnMoreButtonWrapper>
            )}
          </>
        )}
      </GridRow>
    </GridContainer>
  )
}
